import type { SourceErrorCode, SourceState } from '../core/definitions';

export interface AcquiredDevice {
  deviceId: string;
  groupId: string;
}

export interface ReacquireInput {
  kind: 'audioinput' | 'videoinput';
  /** The configured device id; '' follows the system default. */
  configuredId: string;
  acquired: AcquiredDevice | null;
  state: SourceState;
  errorCode?: SourceErrorCode;
  devices: readonly Pick<MediaDeviceInfo, 'deviceId' | 'groupId' | 'kind'>[];
}

/** Error causes that a device appearing can cure. A refused permission or a missing API cannot. */
const RECOVERABLE = new Set<SourceErrorCode | undefined>(['not-found', 'in-use', 'unknown']);

/**
 * Decides, after a `devicechange`, whether a capture source should open its device again:
 * - it lost the device (ended, or failed to find/open it) and a suitable device is back, or
 * - it follows the system default and the default is now a different device.
 */
export function shouldReacquire(input: ReacquireInput): boolean {
  const { kind, configuredId, acquired, state, errorCode, devices } = input;
  const ofKind = devices.filter((d) => d.kind === kind);
  const real = ofKind.filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications' && d.deviceId !== '');

  if (state === 'ended' || (state === 'error' && RECOVERABLE.has(errorCode))) {
    return configuredId === '' ? real.length > 0 : real.some((d) => d.deviceId === configuredId);
  }
  if ((state === 'live' || state === 'starting') && configuredId === '' && acquired) {
    if (kind === 'audioinput') {
      // The 'default' alias carries the group of the device it currently points at.
      const def = ofKind.find((d) => d.deviceId === 'default');
      return Boolean(def?.groupId && acquired.groupId && def.groupId !== acquired.groupId);
    }
    return real.length > 0 && !real.some((d) => d.deviceId === acquired.deviceId);
  }
  return false;
}

export interface DeviceWatchHost {
  kind: 'audioinput' | 'videoinput';
  configuredId(): string;
  acquired(): AcquiredDevice | null;
  status(): { state: SourceState; errorCode?: SourceErrorCode };
  reacquire(): void;
}

/**
 * Listens for `devicechange` and asks the host to re-open its device when `shouldReacquire` says so:
 * replug after unplug, and a changed OS default device. Events arrive in bursts, so they are coalesced.
 */
export class DeviceWatch {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private readonly media: MediaDevices | undefined;

  constructor(
    private readonly host: DeviceWatchHost,
    private readonly debounceMs = 300,
  ) {
    this.media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    this.media?.addEventListener?.('devicechange', this.onChange);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.media?.removeEventListener?.('devicechange', this.onChange);
  }

  private readonly onChange = (): void => {
    if (this.disposed) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.check();
    }, this.debounceMs);
  };

  private async check(): Promise<void> {
    if (this.disposed || !this.media?.enumerateDevices) return;
    let devices: MediaDeviceInfo[];
    try {
      devices = await this.media.enumerateDevices();
    } catch {
      return;
    }
    if (this.disposed) return;
    const status = this.host.status();
    if (
      shouldReacquire({
        kind: this.host.kind,
        configuredId: this.host.configuredId(),
        acquired: this.host.acquired(),
        state: status.state,
        errorCode: status.errorCode,
        devices,
      })
    ) {
      this.host.reacquire();
    }
  }
}

/** Microphone built into the same hardware as a camera: the audio input sharing its `groupId`. */
export function pickPairedAudio(
  devices: readonly Pick<MediaDeviceInfo, 'deviceId' | 'groupId' | 'kind'>[],
  videoGroupId: string | undefined,
): string | null {
  if (!videoGroupId) return null;
  const match = devices.find(
    (d) => d.kind === 'audioinput' && d.groupId === videoGroupId && d.deviceId !== '' && d.deviceId !== 'default' && d.deviceId !== 'communications',
  );
  return match?.deviceId ?? null;
}
